// Candidate-specific Airtable service for questions and timeline updates
// Uses a restricted access token for candidate operations only

import Airtable from 'airtable';
import { TimelineEvent, ApiResponse } from '../types';

// Interface for Questions and Answers table
interface QuestionAnswer {
  id: string;
  questionId: string;
  question: string;
  answer?: string;
  createdDate: Date;
}

class CandidateAirtableService {
  private base: Airtable.Base | null = null;
  private readonly baseId = 'appRQUEimeQjVSXS2';
  private readonly candidateAccessToken = 'patAjPpJXzCVPaaSX.d020a07d003796bbd91a07d5d407ff680e255fdd9d833be1df9cd0666ea6aacc';

  constructor() {
    this.initializeAirtable();
  }

  private initializeAirtable() {
    try {
      Airtable.configure({
        endpointUrl: 'https://api.airtable.com',
        apiKey: this.candidateAccessToken
      });

      this.base = Airtable.base(this.baseId);
      console.log('Candidate Airtable service configured with base:', this.baseId);
    } catch (error) {
      console.error('Failed to configure candidate Airtable service:', error);
    }
  }

  // Helper method to safely transform Airtable records
  private transformRecord(record: any, additionalFields: any = {}): any {
    return {
      id: record.id,
      createdDate: new Date(record.get('Created Date') || Date.now()),
      ...additionalFields
    };
  }

  // Create a question in the Questions and Answers table and link it to a timeline event
  async createQuestionAnswer(
    question: string,
    candidateId: string
  ): Promise<ApiResponse<QuestionAnswer>> {
    try {
      if (!this.base) {
        console.error('❌ Airtable base not configured');
        return { data: {} as QuestionAnswer, success: false, error: 'Airtable not configured' };
      }

      console.log(`📝 Creating new question in Questions and Answers table`);
      console.log(`📝 Question: ${question}`);
      console.log(`📝 Candidate ID: ${candidateId}`);

      // Generate a unique Question ID
      const questionId = `QA_${candidateId}_${Date.now()}`;

      const recordData = {
        fields: {
          'Question ID': questionId,
          'Question': question
          // 'Answer' field will be empty initially - to be filled by admin
        }
      };

      console.log('📝 Question record data to create:', JSON.stringify(recordData, null, 2));

      console.log('📝 Attempting to create record in Questions and Answers table...');
      const records = await this.base('Questions and Answers').create([recordData]);

      if (records.length === 0) {
        return { data: {} as QuestionAnswer, success: false, error: 'Failed to create question record' };
      }

      const record = records[0];
      const newQuestion: QuestionAnswer = {
        id: record.id,
        questionId: record.get('Question ID') as string,
        question: record.get('Question') as string,
        answer: record.get('Answers') as string,
        createdDate: new Date((record.get('Created Date') as string) || Date.now())
      };

      console.log('✅ Successfully created question record');
      console.log('✅ New question record ID:', newQuestion.id);
      return { data: newQuestion, success: true };

    } catch (error) {
      console.error('❌ Error creating question record:', error);

      if (error && typeof error === 'object') {
        console.error('❌ Error details:', JSON.stringify(error, null, 2));
        if ('statusCode' in error) {
          console.error(`❌ HTTP Status: ${error.statusCode}`);
        }
        if ('message' in error) {
          console.error(`❌ Error Message: ${error.message}`);
        }
        if ('error' in error) {
          console.error(`❌ Error Type: ${error.error}`);
        }
      }

      return {
        data: {} as QuestionAnswer,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create question'
      };
    }
  }

  // Create a new timeline event and link it to a Questions and Answers record
  async createQuestionTimelineEvent(
    applicationId: string,
    question: string,
    candidateId: string
  ): Promise<ApiResponse<TimelineEvent>> {
    try {
      if (!this.base) {
        console.error('❌ Airtable base not configured');
        return { data: {} as TimelineEvent, success: false, error: 'Airtable not configured' };
      }

      console.log(`📝 Creating new question timeline event for application: ${applicationId}`);
      console.log(`📝 Question: ${question}`);
      console.log(`📝 Candidate ID: ${candidateId}`);
      console.log(`📝 Base ID: ${this.baseId}`);
      console.log(`📝 Token (first 20 chars): ${this.candidateAccessToken.substring(0, 20)}...`);

      // First, create the question in Questions and Answers table
      console.log('📝 Step 1: Creating question in Questions and Answers table...');
      const questionResponse = await this.createQuestionAnswer(question, candidateId);

      if (!questionResponse.success) {
        console.error('❌ Failed to create question record');
        return {
          data: {} as TimelineEvent,
          success: false,
          error: `Failed to create question record: ${questionResponse.error}`
        };
      }

      const questionRecord = questionResponse.data;
      console.log(`✅ Question record created with ID: ${questionRecord.id}`);

      // Now create the timeline event and link it to the question record
      console.log('📝 Step 2: Creating timeline event and linking to question...');
      const recordData = {
        fields: {
          'Application': [applicationId],
          'Event Type': 'Notes Added',
          'Event Date': new Date().toISOString().split('T')[0],
          'Description': 'Candidate submitted a question',
          'Created By': 'M4 Consultant',
          'Questions and Answers': [questionRecord.id], // Link to the Questions and Answers record
          'Notes': `Question submitted by candidate on ${new Date().toLocaleDateString()}`
        }
      };

      console.log('📝 Timeline event record data to create:', JSON.stringify(recordData, null, 2));

      console.log('📝 Attempting to create record in Timeline Events table...');
      const records = await this.base('Timeline Events').create([recordData]);

      if (records.length === 0) {
        return { data: {} as TimelineEvent, success: false, error: 'Failed to create timeline event' };
      }

      const record = records[0];
      const newEvent = this.transformRecord(record, {
        applicationId: (record.get('Application') as string[])?.[0],
        eventType: record.get('Event Type') as string,
        eventDate: new Date(record.get('Event Date') as string),
        eventTime: record.get('Event Time') as string,
        previousStatus: record.get('Previous Status') as string,
        newStatus: record.get('New Status') as string,
        description: record.get('Description') as string,
        createdBy: record.get('Created By') as string,
        automatedEvent: record.get('Automated Event') as boolean,
        relatedInterviewId: (record.get('Related Interview') as string[])?.[0],
        notes: record.get('Notes') as string,
        currentStep: record.get('Current Step') as string | number,
        candidateImage: record.get('Candidate Image') as any,
        nextSteps: record.get('Next Steps') as string,
        availableMeetTimes: record.get('Available Meet Times') as string,
        candidateQuestions: record.get('Question') as string, // This will be the lookup field
        adminAnswers: record.get('Answer') as string // This will be the lookup field
      });

      console.log('✅ Successfully created question timeline event');
      console.log('✅ New event ID:', newEvent.id);
      console.log('✅ Linked question record ID:', questionRecord.id);
      return { data: newEvent, success: true };

    } catch (error) {
      console.error('❌ Error creating question timeline event:', error);

      // Log detailed error information
      if (error && typeof error === 'object') {
        console.error('❌ Error details:', JSON.stringify(error, null, 2));
        if ('statusCode' in error) {
          console.error(`❌ HTTP Status: ${error.statusCode}`);
        }
        if ('message' in error) {
          console.error(`❌ Error Message: ${error.message}`);
        }
        if ('error' in error) {
          console.error(`❌ Error Type: ${error.error}`);
        }
      }

      return {
        data: {} as TimelineEvent,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create question'
      };
    }
  }

  // Get timeline events for a specific candidate (read-only for verification)
  async getCandidateTimelineEvents(candidateId: string): Promise<ApiResponse<TimelineEvent[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      // This is read-only to verify questions were added successfully
      const records = await this.base('Timeline Events')
        .select({
          maxRecords: 50,
          sort: [{ field: 'Event Date', direction: 'desc' }],
          filterByFormula: `SEARCH("${candidateId}", {Application})`
        })
        .all();

      const events: TimelineEvent[] = records.map((record: any) =>
        this.transformRecord(record, {
          applicationId: (record.get('Application') as string[])?.[0],
          eventType: record.get('Event Type') as string,
          eventDate: new Date(record.get('Event Date') as string),
          eventTime: record.get('Event Time') as string,
          previousStatus: record.get('Previous Status') as string,
          newStatus: record.get('New Status') as string,
          description: record.get('Description') as string,
          createdBy: record.get('Created By') as string,
          automatedEvent: record.get('Automated Event') as boolean,
          relatedInterviewId: (record.get('Related Interview') as string[])?.[0],
          notes: record.get('Notes') as string,
          currentStep: record.get('Current Step') as string | number,
          candidateImage: record.get('Candidate Image') as any,
          nextSteps: record.get('Next Steps') as string,
          availableMeetTimes: record.get('Available Meet Times') as string,
          candidateQuestions: record.get('Questions') as string, // Now lookup from Questions and Answers
          adminAnswers: record.get('Answers') as string // Now lookup from Questions and Answers
        })
      );

      return { data: events, success: true };
    } catch (error) {
      console.error('Error fetching candidate timeline events:', error);
      return {
        data: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch events'
      };
    }
  }

  // Get questions and answers for a specific candidate
  async getCandidateQuestions(candidateId: string): Promise<ApiResponse<QuestionAnswer[]>> {
    try {
      if (!this.base) {
        return { data: [], success: false, error: 'Airtable not configured' };
      }

      console.log(`📝 Fetching questions for candidate: ${candidateId}`);

      // Get questions from the Questions and Answers table
      const records = await this.base('Questions and Answers')
        .select({
          maxRecords: 50,
          sort: [{ field: 'Created Date', direction: 'desc' }],
          filterByFormula: `SEARCH("${candidateId}", {Question ID})`
        })
        .all();

      const questions: QuestionAnswer[] = records.map((record: any) => ({
        id: record.id,
        questionId: record.get('Question ID') as string,
        question: record.get('Question') as string,
        answer: record.get('Answers') as string,
        createdDate: new Date((record.get('Created Date') as string) || Date.now())
      }));

      console.log(`✅ Found ${questions.length} questions for candidate`);
      return { data: questions, success: true };
    } catch (error) {
      console.error('Error fetching candidate questions:', error);
      return {
        data: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch questions'
      };
    }
  }
}

// Export singleton instance
const candidateAirtableService = new CandidateAirtableService();
export default candidateAirtableService;